import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Target, TrendingUp, ShieldCheck, Shield, PiggyBank, Wallet, CreditCard, CalendarClock, Pencil, ChevronRight, Coins, ArrowLeft, ArrowRight, Mail, Lock, Bell, Download, Trash2, AlertTriangle, Sparkles, PauseCircle, PlayCircle, RefreshCw, Heart, LifeBuoy, Lightbulb, Home, Flag, Check, Receipt, CheckCircle2, BadgeCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DashboardShell, PageHead, EmptyState, ErrorState } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { PasswordField } from "@/components/PasswordField";
import { journeyApi, apiError } from "@/lib/journeyApi";
import { useAsyncData } from "@/lib/useAsyncData";
import { fmt } from "@/lib/format";
import { ProgressCenter } from "@/pages/ProgressCenter";
import { MortgageScenario } from "@/components/MortgageScenario";
import { FreedomScenario } from "@/components/FreedomScenario";
import { SlimStepper } from "@/components/DashboardHomeVisuals";
import { ConfirmedOverTime, HorizontalGauge } from "@/components/DashboardCharts";
import { AnimatedBar } from "@/components/AnimatedBar";
import { soundEnabled, setSoundEnabled, celebrate } from "@/lib/celebrate";
import { track } from "@/lib/analytics";

function SoundToggle() {
  const [on, setOn] = useState(soundEnabled());
  const toggle = () => {
    const next = !on;
    setOn(next); setSoundEnabled(next);
    if (next) celebrate("goal");
  };
  return (
    <div className="rounded-2xl pe-surface p-6" data-testid="profile-sound">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#A89A88]">Sound effects</p>
      <div className="mt-3 flex items-center justify-between">
        <p className="text-sm text-[#525252]">Play a short, tasteful cue when you confirm an Edit, protect money, or eliminate a debt.</p>
        <button type="button" role="switch" aria-checked={on} onClick={toggle} data-testid="sound-toggle"
          className={`relative ml-4 h-7 w-12 shrink-0 rounded-full transition-colors ${on ? "bg-[#2F6B57]" : "bg-[#D6CBB8]"}`}>
          <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
        </button>
      </div>
    </div>
  );
}

const INCOME_LABELS = { predictable: "Predictable", variable: "Variable", hybrid: "Hybrid" };

// Restrained, consistent goal identity used everywhere a goal type appears.
export const GOAL_EMOJI = {
  mortgage_freedom: "🏠",
  cash_reserve: "💰",
  major_goal: "🎯",
  custom: "✨",
  financial_independence: "🔥",
};
const goalEmoji = (type) => GOAL_EMOJI[type] || "✨";

// Professional line-icon identity for a goal type (replaces emoji in core UI).
const GOAL_ICON = { mortgage_freedom: Home, cash_reserve: PiggyBank, major_goal: Target, custom: Flag, financial_independence: TrendingUp };
const GoalIcon = ({ type, className }) => { const I = GOAL_ICON[type] || Flag; return <I className={className} />; };

function useFoundation() {
  const [foundation, setFoundation] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    journeyApi.myFoundation()
      .then((d) => { if (!cancelled) setFoundation(d.foundation); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  return { foundation, loading };
}

function SummaryCard({ icon: Icon, label, value, sub, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="group w-full rounded-2xl pe-surface p-5 text-left transition-colors hover:border-[#C9A86A] hover:bg-[#FBF7EF]"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#164C3B]"><Icon className="h-5 w-5" /><p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]">{label}</p></div>
        <Pencil className="h-3.5 w-3.5 text-[#C9A86A] opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
      <p className="mt-3 font-serif text-2xl text-[#171717]">{value}</p>
      {sub && <p className="mt-1 text-sm text-[#70584A]">{sub}</p>}
    </button>
  );
}

export function DashboardHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsyncData(() =>
    Promise.all([journeyApi.myFoundation(), journeyApi.sprintState(), journeyApi.progress()])
      .then(([f, s, p]) => ({ foundation: f.foundation, state: s, prot: s.protection, prog: p }))
  );

  useEffect(() => { track("page_view", { page: "home" }); }, []);

  if (loading) {
    return <DashboardShell wide><PageHead title="Feeling rich?" sub="Tell us what came in and we'll work through what it can do." /><div className="h-40 animate-pulse rounded-2xl bg-[#F0EBE2]" /></DashboardShell>;
  }
  if (error) {
    return (
      <DashboardShell wide>
        <PageHead title="Feeling rich?" sub="Tell us what came in and we'll work through what it can do." />
        <ErrorState onRetry={reload} testid="dashboard-error" />
      </DashboardShell>
    );
  }

  const foundation = data?.foundation || null;
  const prot = data?.prot || null;
  const prog = data?.prog || null;
  const phase = data?.state?.phase;
  const currentStage = data?.state?.current_stage || phase;
  const primaryGoal = data?.state?.primary_goal || null;
  const lastCompletedGoal = data?.state?.last_completed_goal || null;
  const currentTarget = data?.state?.current_target || null;

  const goToMoneyLanded = () => navigate("/dashboard/money-landed");
  const goToGoal = () => navigate("/dashboard/goal");
  const goToAssign = () => navigate("/dashboard/assign");
  const goToProgress = () => navigate("/dashboard/progress");
  const onMaintain = async () => {
    try { await journeyApi.maintainPosition(); toast.success("You're maintaining your position. Change it anytime by setting a new goal."); reload(); }
    catch { toast.error("Could not update. Please try again."); }
  };

  const paused = !!data?.state?.paused;
  // Welcome Back must NOT appear in the SAME session the customer paused in.
  // The pause action sets a session marker; a genuine later return (new session,
  // or after sign-out which clears it) re-enables the banner.
  let pausedThisSession = false;
  try { pausedThisSession = sessionStorage.getItem("pe_paused_session") === "1"; } catch (_) {}
  const showWelcomeBack = !!data?.state?.show_welcome_back && !pausedThisSession;
  // Canonical unassigned money (DERIVED on the server: sum of every actionable
  // source's remainder). Presentation-only reminder; CHOOSE ITS NEXT JOB routes
  // to the real assignment flow.
  const unassignedAmt = Math.round(((data?.state?.unassigned_pool?.total) ?? 0) * 100) / 100;
  const comebacks = data?.state?.comebacks || 0;
  const imBackContinue = async () => {
    try { await journeyApi.imBack({ life_happened: false }); toast.success("Welcome back. Picking up right where you left off."); reload(); }
    catch { toast.error("Could not update. Please try again."); }
  };
  const imBackLife = async () => {
    try { await journeyApi.imBack({ life_happened: true }); navigate("/dashboard/life-happened"); }
    catch { toast.error("Could not update. Please try again."); }
  };

  if (!foundation) {
    return (
      <DashboardShell>
        <PageHead title="Feeling rich?" sub="Tell us what came in and we'll work through what it can do." />
        <EmptyState icon={ShieldCheck} title="No foundation found" body="Set up your Financial Foundation to get started." testid="dashboard-no-foundation" />
      </DashboardShell>
    );
  }

  const income = foundation.income || {};
  const essentials = foundation.essentials || [];
  const debts = foundation.debts || [];
  const safety = foundation.safety || {};
  const essTotal = essentials.reduce((s, e) => s + Number(e.amount || 0), 0);
  const debtTotal = debts.reduce((s, d) => s + Number(d.balance || 0), 0);

  const lifetime = prog?.lifetime || null;
  const startingDebt = prog?.starting_debt || 0;
  const currentDebt = prog?.current_debt || 0;
  const debtEliminatedPhase = currentDebt === 0 && startingDebt > 0;
  const protBelow = prot?.has_recommendation && prot?.state === "below";

  const greeting = user?.first_name ? `Hi, ${user.first_name}` : "Welcome back";
  const isUnassigned = phase === "build_unassigned" && currentStage !== "freedom";
  // Part 18: when unassigned money exists, the UnassignedReminder owns the primary
  // action (CHOOSE ITS NEXT JOB). Do not also surface CHOOSE MY NEXT GOAL here.
  const primaryAction = (isUnassigned && unassignedAmt <= 0)
    ? { label: "Choose my next goal", testid: "dashboard-choose-goal", onClick: goToGoal }
    : { label: "Start an Edit", testid: "money-landed-cta", onClick: goToMoneyLanded };
  // Part 10 / 13: conditional Home hero. Freedom and just-completed states do not
  // use the "Feeling rich?" headline as the dominant line.
  const isFreedom = currentStage === "freedom";
  const justCompleted = !isFreedom && !!lastCompletedGoal && !primaryGoal;
  const heroTitle = isFreedom ? "Maintaining your position." : justCompleted ? "Another goal, handled." : "Feeling rich?";
  const heroSub = isFreedom
    ? "Money came in? You can still start an Edit and decide what you want it to do."
    : justCompleted
      ? `Your ${lastCompletedGoal.name} is fully funded. Decide what comes next when you're ready.`
      : "Got paid, earned a little extra, or have money you can put to work? Start an Edit and see what it could do.";
  const toolRow = "flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition hover:bg-[#F6F2EA]";

  return (
    <DashboardShell wide>
      {/* I'M BACK — intentional return from a pause. Full width, top. */}
      {showWelcomeBack && (
        <div className="mb-6 rounded-2xl border border-[#C9A86A]/60 bg-[#FBF7EF] p-6" data-testid="welcome-back">
          <p className="pe-eyebrow text-[#9A7B3A]">Welcome back</p>
          <h2 className="mt-1.5 font-serif text-2xl text-[#164C3B]">Anything important change since your last Edit?</h2>
          <p className="mt-1 text-sm text-[#525252]">No pressure and no catching up required. Pick up right where you left off.</p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <Button data-testid="welcome-back-nothing" onClick={imBackContinue} className="min-h-11 rounded-xl bg-[#2F6B57] px-5 font-semibold text-white hover:bg-[#255643] sm:w-auto">NOTHING IMPORTANT CHANGED</Button>
            <Button data-testid="welcome-back-life" variant="outline" onClick={imBackLife} className="min-h-11 rounded-xl border-[#2F6B57]/40 px-5 font-semibold text-[#164C3B] hover:bg-white sm:w-auto">YEP, LIFE HAPPENED</Button>
          </div>
        </div>
      )}

      <div className="lg:grid lg:grid-cols-12 lg:items-start lg:gap-6">
        {/* PRIMARY WORKSPACE — one cohesive financial workspace, open sections + hairline dividers. */}
        <div className="space-y-4 lg:col-span-8">
          {unassignedAmt > 0 && <UnassignedReminder amount={unassignedAmt} onChoose={goToAssign} />}

          <section data-testid="home-workspace" className="overflow-hidden rounded-2xl border border-[#ECE6DC] bg-white shadow-[0_18px_50px_-34px_rgba(22,76,59,0.28)]">
            {/* Hero / action band — compact; the single primary action lives here. */}
            <div className="flex flex-col gap-4 px-6 pt-6 pb-6 sm:flex-row sm:items-end sm:justify-between sm:px-8">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#B8923F]">{greeting}</p>
                <h1 className="mt-1.5 font-serif text-3xl leading-tight text-[#171717]">{heroTitle}</h1>
                <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-[#525252]">{heroSub}</p>
              </div>
              <Button data-testid={primaryAction.testid} onClick={primaryAction.onClick}
                className="inline-flex min-h-12 shrink-0 items-center gap-2 rounded-xl bg-[#164C3B] px-7 text-[15px] font-semibold uppercase tracking-[0.04em] text-white hover:bg-[#0f3528]">
                {primaryAction.label} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Current position — ONE merged block: stage + what you're working toward + metrics. */}
            <div className="border-t border-[#ECE6DC] bg-[#FCFBF8] px-6 py-5 sm:px-8">
              <CommandCard
                phase={phase}
                currentStage={currentStage}
                primaryGoal={primaryGoal}
                currentTarget={currentTarget}
                lastCompletedGoal={lastCompletedGoal}
                prot={prot}
                startingDebt={startingDebt}
                onView={goToGoal}
                onSetGoal={goToGoal}
                onReview={goToProgress}
                onMaintain={onMaintain}
              />
            </div>

            {/* Protection — compact secondary status row, integrated (not a detached block). */}
            {prot && (
              <div className="border-t border-[#ECE6DC] px-6 py-4 sm:px-8">
                <HomeProtectionCard prot={prot} safety={safety} onView={() => navigate("/dashboard/foundation/protection?from=home")} />
              </div>
            )}

            {/* Recent confirmation — compact ledger row, inside the workspace. */}
            <RecentConfirmedRow history={prog?.sprint_history || []} onView={() => navigate("/dashboard/progress")} />
          </section>

          {/* Slim path stepper (quiet). */}
          <SlimStepper phase={currentStage} />

          {/* ONE contextual visual — compact confirmed-progress trend (real data only). */}
          {(prog?.sprint_history || []).length >= 2 && (
            <section className="rounded-2xl border border-[#ECE6DC] bg-white p-5 sm:p-6" data-testid="home-trend">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-[#171717]">Money put to work</h3>
                  <p className="mt-0.5 text-xs text-[#8A7C6B]">Cumulative confirmed allocations over time.</p>
                </div>
                <button type="button" onClick={() => navigate("/dashboard/progress")} className="shrink-0 text-sm font-medium text-[#2F6B57] hover:underline" data-testid="home-trend-link">See The Receipts</button>
              </div>
              <div className="mt-3"><ConfirmedOverTime history={prog.sprint_history} testid="home-chart-cumulative" label="Total allocated to date" daily /></div>
            </section>
          )}
        </div>

        {/* SECONDARY RAIL — one compact utility list + continuity, quieter than the decision. */}
        <aside className="mt-4 space-y-4 lg:mt-0 lg:col-span-4">
          <div className="overflow-hidden rounded-2xl border border-[#ECE6DC] bg-[#FCFBF8] divide-y divide-[#ECE6DC]" data-testid="home-tools">
            {(income.type === "variable" || income.type === "hybrid") && (
              <button type="button" onClick={() => navigate("/dashboard/expected-income")} data-testid="home-expected-income" className={toolRow}>
                <span className="flex items-center gap-3">
                  <CalendarClock className="h-5 w-5 shrink-0 text-[#2F6B57]" />
                  <span className="block">
                    <span className="block text-sm font-semibold text-[#171717]">Expecting money soon?</span>
                    <span className="block text-xs text-[#70584A]">Plan for income before it arrives.</span>
                  </span>
                </span>
                <ChevronRight className="h-5 w-5 shrink-0 text-[#C9A86A]" />
              </button>
            )}
            <button type="button" onClick={() => navigate("/dashboard/money-landed?whatif=1")} data-testid="home-whatif" className={toolRow}>
              <span className="flex items-center gap-3">
                <Lightbulb className="h-5 w-5 shrink-0 text-[#2F6B57]" />
                <span className="block">
                  <span className="block text-sm font-semibold text-[#171717]">What if?</span>
                  <span className="block text-xs text-[#70584A]">Explore what an amount could do. Nothing gets saved.</span>
                </span>
              </span>
              <ChevronRight className="h-5 w-5 shrink-0 text-[#C9A86A]" />
            </button>
            <button type="button" onClick={() => navigate("/dashboard/life-happened")} data-testid="home-life-happened" className={toolRow}>
              <span className="flex items-center gap-3">
                <RefreshCw className="h-5 w-5 shrink-0 text-[#2F6B57]" />
                <span className="block">
                  <span className="block text-sm font-semibold text-[#171717]">Life happened?</span>
                  <span className="block text-xs text-[#70584A]">Update the plan. No reset, no judgment.</span>
                </span>
              </span>
              <ChevronRight className="h-5 w-5 shrink-0 text-[#C9A86A]" />
            </button>
            {/* PAUSE — a compact row in the same utility list (not a separate card). */}
            {!paused && <PauseControl onPaused={reload} rowClassName={toolRow} />}
          </div>

          {/* I CAME BACK — gentle recovery acknowledgment, never a streak or badge. */}
          {comebacks > 0 && (
            <div className="rounded-2xl border border-[#DDE7DF] bg-[#F3F7F4] p-4" data-testid="comebacks">
              <p className="flex items-center gap-2 font-serif text-lg text-[#164C3B]" data-testid="comebacks-count">
                I came back <Heart className="h-4 w-4 text-[#C9A86A]" />
                {comebacks > 1 && <span className="font-sans text-sm text-[#70584A]">× {comebacks}</span>}
              </p>
              <p className="mt-1 text-sm text-[#2F6B57]">Life interrupted the plan. You came back. That counts.</p>
            </div>
          )}
        </aside>
      </div>
    </DashboardShell>
  );
}

// Intentional pause. Not failure. Preserves all financial state; optionally
// schedules ONE resume reminder (reuses the notifications check-in system) or
// honors "no reminder" via the existing decline path.
function PauseControl({ onPaused, rowClassName }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [paused, setPausedDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickDate, setPickDate] = useState("");
  const tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; } })();
  const pause = async (opts) => {
    setBusy(true);
    try {
      await journeyApi.pauseSprint({ timezone: tz, ...opts });
      try { sessionStorage.setItem("pe_paused_session", "1"); } catch (_) {}
      toast.success(opts.no_reminder ? "Got it. No reminder scheduled." : "Paused. Everything is saved. Come back whenever you're ready.");
      setPausedDone(true);
    } catch { toast.error("Could not pause right now."); }
    finally { setBusy(false); }
  };
  const signOutNow = async () => { try { await logout(); } catch (_) {} navigate("/login", { replace: true }); };

  // Post-pause confirmation. We deliberately DO NOT show "Welcome Back" here:
  // this is the same session, not a return.
  if (paused) {
    return (
      <div className="bg-[#DDE7DF] p-5" data-testid="home-paused-confirm">
        <p className="font-serif text-xl text-[#164C3B]">You're paused.</p>
        <p className="mt-1 text-sm text-[#2F6B57]">Your plan will be right here when you're ready. Nothing is lost. Nothing needs catching up.</p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <Button data-testid="home-pause-signout" onClick={signOutNow} className="min-h-11 rounded-xl bg-[#2F6B57] px-5 font-semibold text-white hover:bg-[#255643] sm:w-auto">SIGN OUT FOR NOW</Button>
          <Button data-testid="home-pause-stay" variant="ghost" onClick={() => { setPausedDone(false); setOpen(false); onPaused && onPaused(); }} className="min-h-11 rounded-xl text-[#70584A] sm:w-auto">STAY SIGNED IN</Button>
        </div>
      </div>
    );
  }
  if (!open) {
    return (
      <button type="button" data-testid="home-pause-open" onClick={() => setOpen(true)}
        className={rowClassName || "flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition hover:bg-[#F6F2EA]"}>
        <span className="flex items-center gap-3">
          <PauseCircle className="h-5 w-5 shrink-0 text-[#2F6B57]" />
          <span className="block">
            <span className="block text-sm font-semibold text-[#171717]">Need to step away?</span>
            <span className="block text-xs text-[#70584A]">Pause without losing your progress.</span>
          </span>
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-[#C9A86A]" />
      </button>
    );
  }
  return (
    <div className="bg-white p-5" data-testid="home-pause-control">
      <p className="font-medium text-[#171717]">Pause for now. When should we check back?</p>
      <p className="mt-1 text-sm text-[#70584A]">Your Foundation, debts, goals, protection and confirmed history all stay exactly as they are.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {[10, 15, 30].map((d) => (
          <button key={d} type="button" disabled={busy} onClick={() => pause({ in_days: d })} data-testid={`home-pause-${d}`} className="min-h-10 rounded-full border border-[#2F6B57]/40 px-4 text-sm font-medium text-[#164C3B] hover:bg-[#F5F2EC]">{d} days</button>
        ))}
      </div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <input type="date" value={pickDate} onChange={(e) => setPickDate(e.target.value)} data-testid="home-pause-pickdate" className="h-10 rounded-xl pe-surface px-3 text-[#171717] focus:border-[#2F6B57] focus:outline-none" />
        <Button disabled={!pickDate || busy} onClick={() => pause({ date: pickDate })} data-testid="home-pause-pick-save" className="min-h-10 rounded-xl bg-[#2F6B57] px-4 text-sm font-semibold text-white hover:bg-[#255643]">Set date</Button>
      </div>
      <div className="mt-3 flex items-center gap-4">
        <button type="button" disabled={busy} onClick={() => pause({ no_reminder: true })} data-testid="home-pause-none" className="text-sm font-medium text-[#70584A] hover:underline disabled:opacity-50">No reminder</button>
        <button type="button" disabled={busy} onClick={() => setOpen(false)} data-testid="home-pause-cancel" className="text-sm font-medium text-[#A89A88] hover:underline">Never mind</button>
      </div>
    </div>
  );
}

// Merged "Current focus" block inside the workspace. Shows the stage ONCE +
// what you're working toward + aligned metrics. No CTA here (the single primary
// action lives in the hero band); no decorative emoji.
function CommandCard({ phase, currentStage, primaryGoal, currentTarget, lastCompletedGoal, prot, startingDebt, onView, onSetGoal, onReview, onMaintain }) {
  const Eyebrow = ({ icon: Icon, children, tone = "#9A7B3A" }) => (
    <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: tone }}>
      <Icon className="h-3.5 w-3.5" /> {children}
    </p>
  );
  const ViewLink = () => (
    <button type="button" onClick={onView} data-testid="home-priority-view" className="shrink-0 text-sm font-medium text-[#2F6B57] hover:underline">View</button>
  );

  // FREEDOM — current financial stability and choice (customer chose to maintain).
  // Restrained editorial treatment: no celebration screen, no confetti, no gamify.
  if (currentStage === "freedom") {
    return (
      <div data-testid="home-command-card">
        <Eyebrow icon={BadgeCheck} tone="#164C3B">Current focus · Freedom</Eyebrow>
        <p className="mt-2 font-serif text-3xl text-[#164C3B]">Maintaining your position</p>
        <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-[#525252]">Your protection is in place, qualifying consumer debt is cleared, and you're not currently building toward an unmet financial priority. You can maintain your position or choose what comes next.</p>
        <ul className="mt-3 space-y-1.5 text-sm text-[#2F6B57]">
          <li className="flex items-center gap-2" data-testid="freedom-check-protection"><Check className="h-4 w-4 shrink-0" /> Protection covered</li>
          <li className="flex items-center gap-2" data-testid="freedom-check-debt"><Check className="h-4 w-4 shrink-0" /> Qualifying consumer debt cleared</li>
        </ul>
        <div className="mt-4 flex flex-col gap-2.5 sm:flex-row">
          <Button data-testid="freedom-set-new-goal" onClick={onSetGoal} className="min-h-11 rounded-xl bg-[#164C3B] px-5 font-semibold text-white hover:bg-[#0f3528] sm:w-auto">Set a new goal</Button>
          <Button data-testid="freedom-review-position" variant="outline" onClick={onReview} className="min-h-11 rounded-xl border-[#2F6B57]/40 px-5 font-semibold text-[#164C3B] hover:bg-white sm:w-auto">Review your position</Button>
        </div>
      </div>
    );
  }

  // DEBT FREE, no BUILD goal yet (just ran out of debt, or just completed a goal).
  // This is the WHAT'S NEXT decision: set another goal, or maintain the position.
  if (phase === "build_unassigned") {
    const justCompleted = !!lastCompletedGoal;
    return (
      <div data-testid="home-command-card">
        <Eyebrow icon={justCompleted ? Target : ShieldCheck} tone="#164C3B">Current focus · {justCompleted ? "Goal complete" : "Debt cleared"}</Eyebrow>
        {justCompleted ? (
          <>
            <p className="mt-2 flex items-center gap-2.5 font-serif text-3xl text-[#164C3B]"><GoalIcon type={lastCompletedGoal.type} className="h-6 w-6 shrink-0 text-[#2F6B57]" /> {lastCompletedGoal.name}</p>
            <p className="mt-1.5 text-sm text-[#525252]">{fmt(lastCompletedGoal.total_contributed)} funded. Here's what's next.</p>
          </>
        ) : (
          <>
            <p className="mt-2 font-serif text-4xl text-[#164C3B] tabular-nums">{fmt(startingDebt)} <span className="text-[#2F6B57]">→</span> $0</p>
            <p className="mt-1.5 text-sm text-[#525252]">Protection is in place and qualifying debt is cleared. Here's what's next.</p>
          </>
        )}
        <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9A7B3A]">What's next?</p>
        <div className="mt-2 flex flex-col gap-2.5 sm:flex-row">
          <Button data-testid="freedom-set-new-goal" onClick={onSetGoal} className="min-h-11 rounded-xl bg-[#164C3B] px-5 font-semibold text-white hover:bg-[#0f3528] sm:w-auto">Set another goal</Button>
          <Button data-testid="freedom-maintain-position" variant="outline" onClick={onMaintain} className="min-h-11 rounded-xl border-[#2F6B57]/40 px-5 font-semibold text-[#164C3B] hover:bg-white sm:w-auto">Maintain where I am</Button>
        </div>
      </div>
    );
  }

  // ACTIVE BUILD — a Primary Goal is funding.
  if (phase === "build" && primaryGoal) {
    const pct = primaryGoal.open_ended ? null : Math.min(100, Math.max(0, Number(primaryGoal.percent_funded || 0)));
    return (
      <div data-testid="home-command-card">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Eyebrow icon={Target}>Current focus · Build</Eyebrow>
            <p className="mt-2 flex items-center gap-2.5 font-serif text-3xl text-[#171717]"><GoalIcon type={primaryGoal.type} className="h-6 w-6 shrink-0 text-[#2F6B57]" /> {primaryGoal.name}</p>
          </div>
          <ViewLink />
        </div>
        <p className="mt-2 text-sm text-[#525252]">
          {primaryGoal.open_ended ? `${fmt(primaryGoal.funded)} funded so far` : `${fmt(primaryGoal.funded)} of ${fmt(primaryGoal.target)} · ${primaryGoal.percent_funded}% funded`}
          {prot && !protIsBelow(prot) ? "" : ""}
        </p>
        {pct !== null && (
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-[#EDE7DC]"><div className="h-full rounded-full bg-[#2F6B57]" style={{ width: `${pct}%` }} /></div>
        )}
      </div>
    );
  }

  // PROTECTION FIRST — debt free but runway below target.
  if (phase === "protect") {
    return (
      <div data-testid="home-command-card">
        <Eyebrow icon={ShieldCheck}>Current focus · Protect</Eyebrow>
        <p className="mt-2 font-serif text-4xl text-[#171717] tabular-nums">{fmt(prot?.remaining || 0)}</p>
        <p className="mt-1.5 text-sm text-[#525252]">to your protected runway. The Paycheck Edit protects real life first.{primaryGoal ? ` Your ${primaryGoal.name} goal is waiting right behind it.` : ""}</p>
      </div>
    );
  }

  // ELIMINATE — consumer debt remains; the smallest balance is the next target.
  if (phase === "eliminate" && currentTarget) {
    return (
      <div data-testid="home-command-card">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Eyebrow icon={Zap} tone="#164C3B">Current focus · Eliminate</Eyebrow>
            <p className="mt-2 font-serif text-3xl text-[#164C3B]">{currentTarget.name}</p>
            <p className="mt-1.5 text-sm text-[#525252]">{fmt(currentTarget.balance)} remaining</p>
          </div>
          <ViewLink />
        </div>
      </div>
    );
  }

  // Foundation ready, nothing confirmed yet.
  return (
    <div data-testid="home-command-card">
      <Eyebrow icon={ShieldCheck} tone="#164C3B">Current focus · Foundation set</Eyebrow>
      <p className="mt-2 font-serif text-3xl text-[#164C3B]">Your Foundation is ready.</p>
      <p className="mt-1.5 max-w-xl text-sm text-[#525252]">When you get paid, start an Edit and we'll work out what may be available for your next move.</p>
    </div>
  );
}

const protIsBelow = (prot) => prot?.has_recommendation && prot?.state === "below";

// Canonical unassigned money that still needs a destination. Calm, persistent,
// never shaming, never auto-assigned, never counted as deployed progress.
export function UnassignedReminder({ amount, onChoose, compact = false }) {
  if (!(amount > 0)) return null;
  if (compact) {
    return (
      <div className="rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-4" data-testid="unassigned-reminder">
        <p className="text-sm text-[#164C3B]" data-testid="unassigned-amount">{fmt(amount)} still needs a job</p>
        <button type="button" onClick={onChoose} data-testid="unassigned-choose" className="mt-1 text-sm font-semibold text-[#2F6B57] hover:underline">Choose its next job</button>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-[#C9A86A]/60 bg-[#FBF7EF] p-5" data-testid="unassigned-reminder">
      <p className="font-serif text-xl text-[#164C3B]">Your money still needs a job</p>
      <p className="mt-1 text-sm text-[#70584A]"><span data-testid="unassigned-amount" className="font-semibold text-[#164C3B]">{fmt(amount)}</span> is still unassigned from your last Edit. You don't have to decide today. When you're ready, give it a next move.</p>
      <Button data-testid="unassigned-choose" onClick={onChoose} className="mt-4 min-h-11 rounded-xl bg-[#2F6B57] px-5 font-semibold text-white hover:bg-[#255643]">CHOOSE ITS NEXT JOB</Button>
    </div>
  );
}


function HomeProtectionCard({ prot, safety, onView }) {
  const [open, setOpen] = useState(false);
  if (!prot) return null;
  const below = protIsBelow(prot);
  const gap = below ? (prot.remaining || 0) : 0;
  const fmtW = (n) => fmt(Math.round(Number(n || 0)));
  const statusText = below ? `${fmtW(prot.remaining)} remaining to target` : prot.state === "above" ? "Target exceeded" : "Target met";
  return (
    <div data-testid="home-protection">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A7B3A]">
            <ShieldCheck className="h-4 w-4 shrink-0" /> Protection
          </p>
          <p className="mt-1.5 font-serif text-2xl text-[#164C3B]">{below ? "Building your runway" : "Runway covered"}</p>
          <p className="mt-0.5 font-serif text-xl text-[#171717] tabular-nums">{fmtW(prot.funded)}</p>
          <p className="mt-0.5 text-sm text-[#70584A]">Protected against a {fmtW(prot.target)} target · {statusText}</p>
        </div>
        <button type="button" onClick={() => setOpen((v) => !v)} data-testid="home-protection-view" className="shrink-0 text-sm font-medium text-[#2F6B57] hover:underline">{open ? "Hide" : "View"}</button>
      </div>
      {below && (
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-[#EADFC7]"><div className="h-full rounded-full bg-[#C9A86A]" style={{ width: `${prot.percent_funded}%` }} /></div>
      )}
      {open && (
        <div className="mt-4 border-t border-[#F0EBE2] pt-4" data-testid="home-protection-detail">
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-[#70584A]">Recommended target</dt><dd className="font-medium text-[#164C3B]">{fmtW(prot.target)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#70584A]">Currently protected</dt><dd className="font-medium text-[#164C3B]">{fmtW(prot.funded)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#70584A]">Remaining gap</dt><dd className="font-medium text-[#164C3B]" data-testid="home-protection-gap">{fmtW(gap)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#70584A]">Status</dt><dd className="font-semibold text-[#2F6B57]" data-testid="home-protection-status">{below ? "Building your runway" : statusText}</dd></div>
          </dl>
          <button type="button" onClick={onView} data-testid="home-protection-edit" className="mt-3 text-sm font-medium text-[#2F6B57] hover:underline">Edit protection settings</button>
        </div>
      )}
    </div>
  );
}

function CompactFoundation({ income, essTotal, living, prot, debtTotal, onEdit }) {
  const incomeValue = income.type === "variable" ? "Varies by paycheck" : fmt(income.take_home_paycheck || 0);
  const protValue = protIsBelow(prot) ? `${fmt(prot.remaining)} to target` : "Covered ✓";
  const rows = [
    ["Income", INCOME_LABELS[income.type] || "Income", incomeValue, "income"],
    ["Essentials", fmt(essTotal), null, "essentials"],
    ["Living", fmt(living), null, "living"],
    ["Protection", protValue, null, "protection"],
    ["Debt", debtTotal > 0 ? fmt(debtTotal) : "$0 ✓", null, "debts"],
  ];
  return (
    <div className="mt-6 rounded-2xl pe-quiet p-5" data-testid="home-foundation">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#A89A88]">Your foundation</p>
        <button type="button" onClick={() => onEdit("income")} data-testid="home-foundation-edit" className="text-sm font-medium text-[#2F6B57] hover:underline">Edit</button>
      </div>
      <div className="mt-3 divide-y divide-[#F0EBE2]">
        {rows.map(([label, value, sub, section]) => (
          <button key={section} type="button" onClick={() => onEdit(section)} data-testid={`home-foundation-${section}`} className="flex w-full items-center justify-between py-2.5 text-left transition hover:text-[#164C3B]">
            <span className="text-sm text-[#70584A]">{label}</span>
            <span className="text-sm font-medium text-[#171717]">{value}{sub ? <span className="ml-1 text-[#A89A88]">{sub}</span> : null}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// One recent confirmed action — a single quiet row that links to The Receipts.
function RecentConfirmedRow({ history, onView }) {
  if (!history?.length) return null;
  const h = history[history.length - 1];
  let line;
  if (h.completed_goal) line = `${h.goal_name || "Your goal"} complete · ${fmt(h.amount_confirmed)} funded`;
  else if (h.completed_debt) line = `${h.target_name} cleared · ${fmt(h.amount_confirmed)}`;
  else if (h.category === "protection") line = `${fmt(h.amount_confirmed)} → Protected Runway`;
  else if (h.category === "build") line = `${fmt(h.amount_confirmed)} → ${h.goal_name || h.target_name}`;
  else line = `${fmt(h.amount_confirmed)} → ${h.target_name}`;
  const when = fmtDate(h.date);
  return (
    <button type="button" onClick={onView} data-testid="home-recent-confirmed"
      className="flex w-full items-center justify-between gap-3 border-t border-[#ECE6DC] px-6 py-3.5 text-left transition hover:bg-[#FCFBF8] sm:px-8">
      <span className="flex min-w-0 items-center gap-2.5">
        <Receipt className="h-4 w-4 shrink-0 text-[#2F6B57]" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A7B3A]">Recent</span>
        <span className="min-w-0 truncate text-sm font-medium text-[#171717]">{when ? `${when} · ` : ""}{line}</span>
      </span>
      <span className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-[#2F6B57]" data-testid="home-recent-confirmed-link">The Receipts <ArrowRight className="h-4 w-4" /></span>
    </button>
  );
}

// Real authenticated Money Landed entry point. Safe placeholder for now: the
// Sprint engine is out of scope for this task, so this NEVER computes a Sprint,
// manufactures income, or touches the demo-Jordan engine.
export function DashboardMoneyLanded() {
  const navigate = useNavigate();
  const { foundation } = useFoundation();
  const income = foundation?.income || {};
  const expected = income.type !== "variable" && income.take_home_paycheck ? fmt(income.take_home_paycheck) : null;

  return (
    <DashboardShell>
      <button onClick={() => navigate("/dashboard")} data-testid="money-landed-back" className="mb-4 inline-flex items-center gap-1.5 text-sm text-[#70584A] hover:text-[#164C3B]">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </button>
      <PageHead title="Feeling rich?" sub="Tell The Paycheck Edit what came in and we'll help you figure out your next move." />
      <div className="rounded-2xl pe-surface p-8 text-center" data-testid="money-landed-page">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]"><Coins className="h-7 w-7" /></span>
        <p className="mx-auto mt-5 max-w-md font-serif text-2xl text-[#171717]">This is where your Edit begins.</p>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-[#70584A]">
          In a moment you'll confirm the amount that actually landed. The Paycheck Edit will then account for your essentials
          and protection and show what may be available for your next move. Your Foundation is ready and waiting.
        </p>
        {expected && (
          <p className="mx-auto mt-4 max-w-md text-sm text-[#A89A88]">
            Your expected paycheck is {expected}. Nothing is assumed until you confirm what landed.
          </p>
        )}
        <p className="mx-auto mt-5 max-w-md rounded-xl bg-[#F5F2EC] p-3 text-xs leading-relaxed text-[#8A7C6B]">
          The Edit step is being built. The Paycheck Edit will never move money for you or connect to your bank.
        </p>
      </div>
    </DashboardShell>
  );
}

const STATUS_LABEL = { recommended: "Proposed", customer_accepted: "Accepted", awaiting_confirmation: "Awaiting confirmation", completed: "Completed", kept_protected: "Kept protected", superseded: "Superseded", cancelled: "Cancelled" };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return ""; } };

export function DashboardSprint() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsyncData(() =>
    Promise.all([journeyApi.sprintState(), journeyApi.progress()]).then(([s, p]) => ({ state: s, progress: p }))
  );
  const state = data?.state || null;
  const progress = data?.progress || null;

  const active = state?.active_sprint;
  const primaryGoal = state?.primary_goal;
  const phase = state?.phase;
  const activeLive = active && ["recommended", "customer_accepted", "awaiting_confirmation"].includes(active.status);

  return (
    <DashboardShell>
      <PageHead title="My Edit" backToHome />
      {loading ? (
        <div className="h-24 animate-pulse rounded-2xl bg-[#F0EBE2]" />
      ) : error ? (
        <ErrorState onRetry={reload} testid="sprint-error" />
      ) : (
        <>
          {phase === "build" && primaryGoal && (
            <div className="mb-5 rounded-2xl border border-[#ECE6DC] bg-[#FCFBF8] px-5 py-4" data-testid="sprint-goal-context">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A7B3A]">Current focus · {phase === "build" ? "Build" : phase}</p>
              <p className="mt-1 font-serif text-lg text-[#171717]">{primaryGoal.name}{primaryGoal.percent_funded != null ? <span className="ml-2 font-sans text-sm text-[#8A7C6B]">{primaryGoal.percent_funded}% funded</span> : null}</p>
              <p className="mt-0.5 text-sm text-[#70584A]">New Edits will propose money toward this goal.</p>
            </div>
          )}
          {phase === "build_unassigned" && (
            <div className="mb-6 rounded-2xl border border-[#2F6B57]/40 bg-[#DDE7DF] p-5" data-testid="sprint-no-goal">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#164C3B]">No Primary Goal</p>
              <p className="mt-1 font-serif text-2xl text-[#164C3B]">{state?.last_completed_goal ? "That last one is done." : "Your debt is clear."}</p>
              <p className="mt-1 text-sm text-[#2F6B57]">Choose where you want your next Edit to go.</p>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button data-testid="sprint-choose-goal" onClick={() => navigate("/dashboard/goal")} className="min-h-11 w-full rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643] sm:w-auto">CHOOSE MY NEXT GOAL</Button>
                <Button data-testid="sprint-money-landed" variant="outline" onClick={() => navigate("/dashboard/money-landed")} className="min-h-11 w-full rounded-xl border-[#2F6B57]/40 bg-white px-6 font-semibold text-[#164C3B] hover:bg-white/60 sm:w-auto">START MY EDIT</Button>
              </div>
            </div>
          )}
          {activeLive ? (
            <div className="rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-6" data-testid="sprint-active">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#9A7B3A]">Edit in progress</p>
              <p className="mt-2 font-serif text-2xl text-[#164C3B]">{fmt(active.potential_sprint_money)} available for this Edit</p>
              <p className="mt-1 text-sm text-[#70584A]">{active.status === "awaiting_confirmation" ? "Review your proposed allocation and approve it. Nothing is confirmed yet." : "You have a proposed Edit to review. Nothing is confirmed yet."}</p>
              <Button data-testid="sprint-resume" onClick={() => navigate("/dashboard/money-landed")} className="mt-4 min-h-11 rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643]">CONTINUE EDIT</Button>
            </div>
          ) : (
            <div className="rounded-2xl pe-surface px-7 py-6 text-center sm:px-9 sm:py-7" data-testid="sprint-empty">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]"><Zap className="h-6 w-6" /></span>
              <h2 className="mt-4 font-serif text-2xl text-[#171717]">Ready for your next Edit.</h2>
              <p className="mx-auto mt-2 max-w-md text-[15px] leading-relaxed text-[#525252]">Enter the money you've received and review where it could go.</p>
              <Button data-testid="sprint-money-landed" onClick={() => navigate("/dashboard/money-landed")} className="mt-6 min-h-12 rounded-full bg-[#2F6B57] px-9 text-base font-semibold tracking-wide text-white hover:bg-[#255643]">START MY EDIT</Button>
              <p className="mt-4 text-xs text-[#A89A88]">Nothing is confirmed until you approve your Edit.</p>
            </div>
          )}

          {progress && (() => {
            const hist = progress.sprint_history || [];
            if (!hist.length) return null;
            const h = [...hist].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
            const dest = h.category === "protection" ? "Protected Runway" : h.category === "build" ? (h.goal_name || h.target_name) : h.target_name;
            const d = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return ""; } };
            return (
              <div className="mt-8 rounded-2xl border border-[#ECE6DC] bg-[#FCFBF8] p-5" data-testid="sprint-history">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A7B3A]">Last confirmed Edit</p>
                <p className="mt-2 font-serif text-2xl text-[#164C3B] tabular-nums">{fmt(h.amount_confirmed)}</p>
                <p className="mt-0.5 text-sm text-[#70584A]">Toward {dest} · {d(h.date)}{h.completed_goal ? " · goal complete" : h.completed_debt ? " · debt eliminated" : ""}</p>
                <button type="button" data-testid="sprint-see-receipts" onClick={() => navigate("/dashboard/progress")}
                  className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#2F6B57] hover:underline">View all in The Receipts <ArrowRight className="h-4 w-4" /></button>
              </div>
            );
          })()}
        </>
      )}
    </DashboardShell>
  );
}

export function DashboardGoal() {
  const { data, loading, error, reload } = useAsyncData(() => journeyApi.sprintState());
  const [override, setOverride] = useState(null);
  const navigate = useNavigate();
  const state = override || data;

  if (loading) return <DashboardShell><PageHead title="My Goal" backToHome /><div className="h-24 animate-pulse rounded-2xl bg-[#F0EBE2]" /></DashboardShell>;
  if (error) return <DashboardShell><PageHead title="My Goal" backToHome /><ErrorState onRetry={reload} testid="goal-error" /></DashboardShell>;

  const target = state?.current_target;
  const noFoundation = !state?.foundation_ready;
  const prot = state?.protection;
  const primaryGoal = state?.primary_goal;
  const protBelow = prot?.has_recommendation && prot?.state === "below";
  const unassignedAmt = Math.round(((state?.unassigned_pool?.total) ?? 0) * 100) / 100;
  const completedGoals = state?.completed_goals || [];
  const allGoalsTotal = completedGoals.reduce((s, g) => s + (g.total_contributed || 0), 0) + (primaryGoal?.funded || 0);

  return (
    <DashboardShell>
      <PageHead title="My Goal" backToHome />
      {unassignedAmt > 0 && <div className="mb-4"><UnassignedReminder amount={unassignedAmt} onChoose={() => navigate("/dashboard/assign")} compact /></div>}
      {noFoundation ? (
        <EmptyState icon={Target} title="Set up your Foundation first." body="Once your Financial Foundation is ready, your goal appears here." testid="goal-empty" />
      ) : target ? (
        // ELIMINATE: consumer debt is the current target. Protection (for variable
        // and hybrid income) is reserved automatically when money lands; it is a
        // secondary notice here, never a replacement for the debt target.
        <>
          <div className="rounded-2xl pe-surface p-6" data-testid="goal-current">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#C9A86A]">Eliminate · current target</p>
            <p className="mt-2 font-serif text-3xl text-[#171717]">{target.name}</p>
            {prot?.fully_funded && <p className="mt-1 text-sm text-[#2F6B57]">Protected Runway fully funded. Now we attack debt.</p>}
            {(() => {
              const start = target.original_balance ?? target.balance;
              const cur = target.balance;
              const paid = Math.max(start - cur, 0);
              const pct = start > 0 ? Math.round((paid / start) * 100) : 0;
              return (
                <>
                  <div className="mt-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
                    <div className="min-w-0"><p className="text-[#A89A88]">Starting</p><p className="font-medium text-[#171717] break-words">{fmt(start)}</p></div>
                    <div className="min-w-0"><p className="text-[#A89A88]">Current</p><p className="font-medium text-[#164C3B] break-words">{fmt(cur)}</p></div>
                    <div className="min-w-0"><p className="text-[#A89A88]">Paid down</p><p className="font-medium text-[#171717] break-words">{fmt(paid)}</p></div>
                  </div>
                  <div className="mt-4"><AnimatedBar value={pct} id={`debt-${target?.id || "current"}`} trackClassName="h-2.5 w-full overflow-hidden rounded-full bg-[#F0EBE2]" barClassName="h-full rounded-full bg-[#2F6B57]" testid="goal-progress-bar" /></div>
                  <p className="mt-2 text-sm text-[#70584A]">{pct}% eliminated. Smallest balance first, then we roll to the next.</p>
                </>
              );
            })()}
          </div>
          {protBelow && <ProtectionNotice prot={prot} />}
        </>
      ) : primaryGoal ? (
        // BUILD: the selected Primary Goal is always the headline. If Protection
        // still needs attention it appears as a secondary notice underneath, it
        // never hides the goal the customer chose.
        <>
          <BuildGoalProgress goal={primaryGoal} onEdited={reload} />
          {protBelow && <ProtectionNotice prot={prot} />}
          <GoalCompletedList goals={completedGoals} allGoalsTotal={allGoalsTotal} />
        </>
      ) : (state?.phase === "build_unassigned" || state?.phase === "build" || state?.phase === "protect") ? (
        <>
          <BuildGoalPicker mortgage={state?.foundation?.mortgage} pausedGoals={state?.paused_goals} onChosen={(st) => setOverride({ ...state, phase: st.phase, primary_goal: st.primary_goal })} />
          {protBelow && <ProtectionNotice prot={prot} />}
          <GoalCompletedList goals={completedGoals} allGoalsTotal={allGoalsTotal} />
        </>
      ) : (
        <>
          <EmptyState icon={Target} title="No debts to target." body="You entered no consumer debts. Once your Foundation is protected, you'll choose a BUILD goal here." testid="goal-no-debt" />
          <GoalCompletedList goals={completedGoals} allGoalsTotal={allGoalsTotal} />
        </>
      )}
    </DashboardShell>
  );
}

// Protection appears as a calm, secondary requirement. Clear and trustworthy
// tone (not a joke): it informs, it does not replace the Primary Goal.
function ProtectionNotice({ prot }) {
  return (
    <div className="mt-4 rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-5" data-testid="goal-protection-notice">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]"><Shield className="h-4 w-4" /> Protection check</p>
      <p className="mt-2 text-sm text-[#70584A]">The Paycheck Edit recommends adding <span className="font-semibold text-[#164C3B]">{fmt(prot.remaining)}</span> to your Protected Runway before your next goal allocation. Protection can receive first priority without changing your goal.</p>
      <div className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
        <div className="min-w-0"><p className="text-[#A89A88]">Protected</p><p className="font-medium text-[#164C3B] break-words">{fmt(prot.funded)}</p></div>
        <div className="min-w-0"><p className="text-[#A89A88]">Recommended</p><p className="font-medium text-[#171717] break-words">{fmt(prot.target)}</p></div>
        <div className="min-w-0"><p className="text-[#A89A88]">To target</p><p className="font-medium text-[#171717] break-words">{fmt(prot.remaining)}</p></div>
      </div>
    </div>
  );
}

const BUILD_GOALS = [
  { type: "mortgage_freedom", label: "Mortgage Freedom", desc: "Knock down your mortgage balance", needsTarget: true, placeholder: "Mortgage balance" },
  { type: "cash_reserve", label: "Cash Reserve", desc: "Build a savings cushion", needsTarget: true, placeholder: "Savings target" },
  { type: "major_goal", label: "Major Goal", desc: "A car, a wedding, a big plan", needsTarget: true, placeholder: "Goal amount" },
  { type: "custom", label: "Custom Goal", desc: "Name your own", needsTarget: false, placeholder: "Target (optional)" },
  { type: "financial_independence", label: "Financial Independence", desc: "Work toward work-optional", needsTarget: false, placeholder: "Target (auto if blank)" },
];

function BuildGoalPicker({ onChosen, mortgage, pausedGoals }) {
  const [sel, setSel] = useState(null);
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [annualSpending, setAnnualSpending] = useState("");
  const [withdrawalRate, setWithdrawalRate] = useState("");
  const [currentAssets, setCurrentAssets] = useState("");
  const [fiMonthly, setFiMonthly] = useState("");
  const [fiGrowth, setFiGrowth] = useState("");
  const [busy, setBusy] = useState(false);
  const mortgageBalance = Number(mortgage?.balance || 0);
  const formRef = useRef(null);
  const nameRef = useRef(null);

  // When a goal is picked, bring its setup form into view and focus the first
  // field so the customer is moved straight into completing that goal.
  useEffect(() => {
    if (!sel) return;
    const t = setTimeout(() => {
      try { formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); nameRef.current?.focus({ preventScroll: true }); } catch {}
    }, 80);
    return () => clearTimeout(t);
  }, [sel]);

  const pick = (g) => {
    setSel(g);
    // Prepopulate the standard goal name so the customer isn't retyping the type
    // they just picked. Custom stays blank. Any manual edit afterward is kept.
    setName(g.type === "custom" ? "" : g.label);
    // Mortgage Freedom prefills the target from the stored mortgage balance.
    setTarget(g.type === "mortgage_freedom" && mortgageBalance > 0 ? String(mortgageBalance) : "");
    setAnnualSpending("");
    setWithdrawalRate("");
    setCurrentAssets("");
    setFiMonthly("");
    setFiGrowth("");
  };

  const choose = async () => {
    if (!sel) return;
    if (sel.type === "financial_independence" && !withdrawalRate) {
      toast.error("Choose a withdrawal rate to continue.");
      return;
    }
    setBusy(true);
    try {
      const payload = { type: sel.type, name: name || sel.label, target: target ? Number(target) : null };
      if (sel.type === "financial_independence") {
        payload.annual_spending = annualSpending ? Number(annualSpending) : null;
        payload.withdrawal_rate = withdrawalRate ? Number(withdrawalRate) : null;
        payload.current_assets = currentAssets ? Number(currentAssets) : null;
        payload.monthly_contribution = fiMonthly ? Number(fiMonthly) : null;
        payload.growth_rate = fiGrowth ? Number(fiGrowth) : null;
      }
      const st = await journeyApi.setGoal(payload);
      track("goal_set", { goal_type: sel.type });
      toast.success("BUILD goal set.");
      onChosen(st);
    } catch (e) { toast.error(apiError(e, "Could not set goal.")); } finally { setBusy(false); }
  };

  const resume = async (g) => {
    setBusy(true);
    try {
      const st = await journeyApi.setGoal({ resume_goal_id: g.id });
      toast.success(`Resumed ${g.name}.`);
      onChosen(st);
    } catch (e) { toast.error(apiError(e, "Could not resume goal.")); } finally { setBusy(false); }
  };

  return (
    <div data-testid="build-goal-picker">
      {pausedGoals?.length > 0 && (
        <div className="mb-5 rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-5" data-testid="paused-goals">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#9A7B3A]">Resume a paused goal</p>
          <div className="mt-3 space-y-2">
            {pausedGoals.map((g) => (
              <div key={g.id} className="flex items-center justify-between rounded-xl pe-surface p-3" data-testid={`paused-goal-${g.id}`}>
                <div>
                  <p className="font-medium text-[#171717]">{g.name}</p>
                  <p className="text-xs text-[#A89A88]">{g.open_ended ? `${fmt(g.funded)} built so far` : `${fmt(g.funded)} of ${fmt(g.target)} · ${g.percent_funded}%`}</p>
                </div>
                <Button data-testid={`resume-goal-${g.id}`} disabled={busy} onClick={() => resume(g)} className="h-9 rounded-xl bg-[#C9A86A] px-4 text-sm font-semibold text-[#3a2d13] hover:bg-[#b8974f]">Resume</Button>
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm text-[#8A7C6B]">Or choose a different goal below.</p>
        </div>
      )}
      <div className="rounded-2xl border border-[#DDE7DF] bg-[#DDE7DF] p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#164C3B]">Your consumer debt is cleared</p>
        <p className="mt-2 font-serif text-2xl text-[#164C3B]">You've completed Eliminate and unlocked Build.</p>
        <p className="mt-1 text-[#2F6B57]">Now choose what you'd like to work toward next.</p>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {BUILD_GOALS.map((g) => (
          <button key={g.type} type="button" data-testid={`build-goal-${g.type}`} onClick={() => pick(g)}
            className={`rounded-2xl border p-5 text-left transition-all ${sel?.type === g.type ? "border-[#2F6B57] bg-[#F5F2EC] ring-1 ring-[#2F6B57]" : "border-[#E5E0D9] bg-white hover:border-[#C9A86A]/60"}`}>
            <p className="flex items-center gap-2 font-serif text-lg text-[#171717]"><GoalIcon type={g.type} className="h-5 w-5 shrink-0 text-[#2F6B57]" /> {g.label}</p>
            <p className="mt-1 text-sm text-[#70584A]">{g.desc}</p>
            {g.type === "mortgage_freedom" && mortgageBalance > 0 && <p className="mt-1 text-xs font-medium text-[#9A7B3A]">Prefilled from your mortgage: {fmt(mortgageBalance)}</p>}
          </button>
        ))}
      </div>
      {sel && (
        <form ref={formRef} onSubmit={(e) => { e.preventDefault(); choose(); }} className="mt-5 rounded-2xl pe-surface p-5" data-testid="build-goal-form">
          <label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]">Goal name</label>
          <input ref={nameRef} enterKeyHint="next" value={name} onChange={(e) => setName(e.target.value)} placeholder={sel.label} data-testid="build-goal-name" className="mt-1 w-full rounded-xl border border-[#E5E0D9] px-4 py-2.5 text-[#171717] outline-none focus:border-[#2F6B57]" />

          {sel.type === "financial_independence" ? (
            <div data-testid="fi-assumptions">
              <label className="mt-3 block text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]">Desired annual spending in retirement</label>
              <input value={annualSpending} onChange={(e) => setAnnualSpending(e.target.value.replace(/[^0-9.]/g, ""))} inputMode="decimal" placeholder="$ per year" data-testid="fi-annual-spending" className="mt-1 w-full rounded-xl border border-[#E5E0D9] px-4 py-2.5 text-[#171717] outline-none focus:border-[#2F6B57]" />
              <label className="mt-3 block text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]">Your withdrawal-rate assumption</label>
              <select value={withdrawalRate} onChange={(e) => setWithdrawalRate(e.target.value)} data-testid="fi-withdrawal-rate" className="mt-1 w-full rounded-xl border border-[#E5E0D9] px-4 py-2.5 text-[#171717] outline-none focus:border-[#2F6B57]">
                <option value="" disabled>Choose one</option>
                {["3", "3.5", "4", "4.5", "5"].map((r) => <option key={r} value={r}>{r}% per year</option>)}
              </select>
              <label className="mt-3 block text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]">Assets already earmarked for independence (optional)</label>
              <input value={currentAssets} onChange={(e) => setCurrentAssets(e.target.value.replace(/[^0-9.]/g, ""))} inputMode="decimal" placeholder="$ current total" data-testid="fi-current-assets" className="mt-1 w-full rounded-xl border border-[#E5E0D9] px-4 py-2.5 text-[#171717] outline-none focus:border-[#2F6B57]" />
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]">Add per month (optional)</label>
                  <input value={fiMonthly} onChange={(e) => setFiMonthly(e.target.value.replace(/[^0-9.]/g, ""))} inputMode="decimal" placeholder="$ / month" data-testid="fi-monthly" className="mt-1 w-full rounded-xl border border-[#E5E0D9] px-4 py-2.5 text-[#171717] outline-none focus:border-[#2F6B57]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]">Growth assumption (optional)</label>
                  <input value={fiGrowth} onChange={(e) => setFiGrowth(e.target.value.replace(/[^0-9.]/g, ""))} inputMode="decimal" placeholder="% / year" data-testid="fi-growth" className="mt-1 w-full rounded-xl border border-[#E5E0D9] px-4 py-2.5 text-[#171717] outline-none focus:border-[#2F6B57]" />
                </div>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-[#8A7C6B]">Your withdrawal rate determines the estimated amount needed to support your chosen annual spending. This is a planning assumption, not a guaranteed retirement outcome. The Paycheck Edit does not recommend investments, asset allocations, securities, or guaranteed outcomes.</p>
            </div>
          ) : (
            <>
              <label className="mt-3 block text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]">{sel.placeholder}</label>
              <input value={target} onChange={(e) => setTarget(e.target.value.replace(/[^0-9.]/g, ""))} inputMode="decimal" placeholder="$" data-testid="build-goal-target" className="mt-1 w-full rounded-xl border border-[#E5E0D9] px-4 py-2.5 text-[#171717] outline-none focus:border-[#2F6B57]" />
              {sel.type === "mortgage_freedom" && <MortgageScenario compact />}
            </>
          )}
          <Button type="submit" data-testid="build-goal-save" disabled={busy} className="mt-4 min-h-11 w-full rounded-xl bg-[#2F6B57] font-semibold text-white hover:bg-[#255643]">{sel.type === "financial_independence" ? "USE THIS SCENARIO" : "CHOOSE MY NEXT GOAL"}</Button>
        </form>
      )}
    </div>
  );
}

function GoalCompletedList({ goals, allGoalsTotal }) {
  if (!goals?.length) return null;
  return (
    <section className="mt-6 rounded-2xl border border-[#ECE6DC] bg-white p-5 sm:p-6" data-testid="goal-completed-list">
      {allGoalsTotal > 0 && (
        <p className="mb-3 text-sm text-[#8A7C6B]">Across all your goals, you've confirmed {fmt(Math.round(allGoalsTotal))} in allocations.</p>
      )}
      <div className="flex items-baseline justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-[#9A7B3A]">Completed goals</h3>
        <span className="text-xs text-[#8A7C6B]">{goals.length} completed</span>
      </div>
      <div className="mt-3 divide-y divide-[#F0EBE2]">
        {[...goals].reverse().map((g) => (
          <div key={g.id} className="flex items-center justify-between gap-3 py-3" data-testid={`goal-completed-${g.id}`}>
            <span className="flex min-w-0 items-center gap-2.5">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-[#2F6B57]" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-[#171717]">{g.name}</span>
                <span className="block text-xs text-[#8A7C6B]">Completed in {g.completed_count} {g.completed_count === 1 ? "Edit" : "Edits"}</span>
              </span>
            </span>
            <span className="shrink-0 font-serif text-lg text-[#164C3B] tabular-nums">{fmt(Math.round(g.total_contributed))}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function BuildGoalProgress({ goal, onEdited }) {
  const pct = goal.percent_funded;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(goal.name || "");
  const [target, setTarget] = useState(goal.open_ended ? "" : String(goal.target || ""));
  const [busy, setBusy] = useState(false);
  const { data: prog } = useAsyncData(() => journeyApi.progress());
  const history = (prog?.sprint_history || []).filter((h) => h.category === "build" && (goal.id ? (h.goal_id === goal.id || !h.goal_id) : true));
  const editCount = history.length;

  const save = async () => {
    setBusy(true);
    try {
      const payload = { edit: true, name: name.trim() || goal.name };
      if (!goal.open_ended) {
        const t = parseFloat(target);
        if (!Number.isFinite(t) || t <= 0) { toast.error("Enter a valid target amount."); setBusy(false); return; }
        if (t < (goal.funded || 0)) { toast.error(`Target can't be below what you've already funded (${fmt(goal.funded)}).`); setBusy(false); return; }
        payload.target = t;
      }
      await journeyApi.setGoal(payload);
      toast.success("Goal updated. Your progress is preserved.");
      setEditing(false);
      onEdited && onEdited();
    } catch (e) { toast.error(apiError(e, "Could not update your goal.")); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#C9A86A]/50 bg-[#FBF7EF] p-6" data-testid="goal-build">
      <div className="flex items-center justify-end">
        {!editing && (
          <button type="button" onClick={() => setEditing(true)} data-testid="build-goal-edit" className="text-xs font-medium text-[#70584A] underline underline-offset-4 hover:text-[#164C3B]">Edit goal</button>
        )}
      </div>
      {editing ? (
        <div className="mt-4 space-y-3" data-testid="build-goal-edit-form">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">Goal name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="build-goal-edit-name" className="mt-1 min-h-11 w-full rounded-xl pe-surface px-3.5 text-[#171717] focus:border-[#2F6B57] focus:outline-none" />
          </div>
          {!goal.open_ended && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">Target amount</label>
              <input inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value.replace(/[^0-9.]/g, ""))} data-testid="build-goal-edit-target" className="mt-1 min-h-11 w-full rounded-xl pe-surface px-3.5 text-[#171717] focus:border-[#2F6B57] focus:outline-none" />
              <p className="mt-1 text-xs text-[#A89A88]">Editing keeps every confirmed contribution. You've funded {fmt(goal.funded)} so far.</p>
            </div>
          )}
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button data-testid="build-goal-save-edit" disabled={busy} onClick={save} className="min-h-11 w-full rounded-xl bg-[#2F6B57] font-semibold text-white hover:bg-[#255643] sm:w-auto sm:px-6">Save changes</Button>
            <Button variant="ghost" disabled={busy} onClick={() => setEditing(false)} className="min-h-11 w-full rounded-xl text-[#70584A] hover:bg-[#F0EBE2] sm:w-auto sm:px-6">Cancel</Button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9A7B3A]">Current goal · Build</p>
          <p className="mt-1.5 flex items-center gap-2.5 font-serif text-3xl text-[#171717]"><GoalIcon type={goal.type} className="h-6 w-6 shrink-0 text-[#2F6B57]" /> {goal.name}</p>
          <div className="mt-4">
            <HorizontalGauge value={goal.funded || 0} target={goal.open_ended ? 0 : goal.target || 0} pct={goal.open_ended ? null : pct} testid="goal-build-gauge" />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-x-6 border-t border-[#F0EBE2] pt-4">
            <div className="min-w-0"><p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#A89A88]">Remaining</p><p className="mt-0.5 font-serif text-xl text-[#171717] tabular-nums">{goal.open_ended ? "—" : fmt(goal.remaining)}</p><p className="mt-0.5 text-xs text-[#8A7C6B]">Until your goal is funded</p></div>
            <div className="min-w-0"><p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#A89A88]">Confirmed Edits</p><p className="mt-0.5 font-serif text-xl text-[#164C3B] tabular-nums">{editCount}</p><p className="mt-0.5 text-xs text-[#8A7C6B]">Toward this goal</p></div>
          </div>
          {history.length > 0 && (
            <div className="mt-5 border-t border-[#F0EBE2] pt-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#9A7B3A]">Contribution history</p>
              <p className="mt-0.5 mb-2 text-sm text-[#8A7C6B]">Your confirmed contributions, Edit by Edit.</p>
              <ConfirmedOverTime history={history} testid="goal-build-journey" label="Total funded" bySequence />
              <p className="mt-3 text-sm text-[#70584A]">{editCount} confirmed {editCount === 1 ? "Edit" : "Edits"}. {fmt(goal.funded)} toward your goal. Every contribution shown here was confirmed by you.</p>
            </div>
          )}
          {goal.type === "mortgage_freedom" && <MortgageScenario />}
          {goal.type === "financial_independence" && <FreedomScenario />}
        </>
      )}
    </div>
  );
}

export function DashboardProgress() {
  const { data, loading, error, reload } = useAsyncData(() => journeyApi.progress());
  const [comebacks, setComebacks] = useState(0);
  useEffect(() => { journeyApi.continuity().then((c) => setComebacks(c.comebacks || 0)).catch(() => {}); }, []);

  if (loading) return <DashboardShell><PageHead title="Progress" backToHome /><div className="h-24 animate-pulse rounded-2xl bg-[#F0EBE2]" /></DashboardShell>;
  if (error) return <DashboardShell><PageHead title="Progress" backToHome /><ErrorState onRetry={reload} testid="progress-error" /></DashboardShell>;

  const prot = data?.protection;
  const hasProtection = prot && (prot.completed_count > 0 || prot.funded > 0);
  const hasAny = data && (data.sprints_completed > 0 || hasProtection || !!data.goal || (data.goals?.completed || []).length > 0);

  if (!hasAny) {
    return (
      <DashboardShell>
        <PageHead title="Progress" backToHome />
        <EmptyState icon={TrendingUp} title="Nothing to show yet." body="Every confirmed payment and every dollar you protect shows up here so you can watch the momentum build over time." testid="progress-empty" />
      </DashboardShell>
    );
  }

  return (
    <DashboardShell wide>
      <ProgressCenter data={data} />
    </DashboardShell>
  );
}

// THE RECEIPTS — a presentation layer over canonical confirmed history. It adds
// NO calculation and is NOT a source of truth: every figure is read straight
// from /me/progress canonical confirmed metrics (lifetime totals, confirmed
// debt before/now, eliminated/completed counts, confirmed Edits) plus the
// idempotent recovery count. It never shows proposed/awaiting/cancelled/
// superseded/What-If/unassigned amounts.
function ReceiptsHero({ data, comebacks }) {
  const lt = data?.lifetime || {};
  const startingDebt = data?.starting_debt || 0;
  const currentDebt = data?.current_debt || 0;
  const money = (v) => fmt(v || 0);
  const tiles = [
    { label: "Put to work", value: lt.money_put_to_work },
    { label: "Protected", value: lt.protected },
    { label: "Debt attacked", value: lt.debt_attacked },
    { label: "Built toward goals", value: lt.goals_built },
  ].filter((t) => (t.value || 0) > 0);
  return (
    <div data-testid="the-receipts" className="mb-8 overflow-hidden rounded-3xl border border-[#164C3B] bg-[#164C3B] text-white">
      <div className="p-7 sm:p-9">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A86A]">The Receipts</p>
        <h2 className="mt-2 font-serif text-3xl sm:text-4xl">Proof, not projections.</h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/70">Every number here comes only from money you actually confirmed. Nothing proposed, pending, simulated, or unassigned counts.</p>
        {startingDebt > 0 && (
          <div className="mt-6 flex flex-wrap items-end gap-x-6 gap-y-2" data-testid="receipts-before-now">
            <div><p className="text-[11px] uppercase tracking-[0.14em] text-white/55">Before</p><p className="font-serif text-2xl text-white/75 line-through decoration-white/25">{money(startingDebt)}</p></div>
            <ArrowRight className="mb-1 h-5 w-5 text-[#C9A86A]" />
            <div><p className="text-[11px] uppercase tracking-[0.14em] text-[#C9A86A]">Now</p><p className="font-serif text-3xl" data-testid="receipts-current-debt">{money(currentDebt)}</p></div>
            <p className="mb-1.5 text-sm text-white/60">consumer debt</p>
          </div>
        )}
        {tiles.length > 0 && (
          <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="receipts-tiles">
            {tiles.map((t) => (
              <div key={t.label} className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
                <p className="font-serif text-2xl">{money(t.value)}</p>
                <p className="mt-0.5 text-xs text-white/60">{t.label}</p>
              </div>
            ))}
          </div>
        )}
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
          <span>{lt.debts_eliminated || 0} {(lt.debts_eliminated || 0) === 1 ? "debt" : "debts"} eliminated</span>
          <span>{lt.goals_completed || 0} {(lt.goals_completed || 0) === 1 ? "goal" : "goals"} completed</span>
          <span>{lt.confirmed_sprints || 0} {(lt.confirmed_sprints || 0) === 1 ? "Edit" : "Edits"} completed</span>
          {comebacks > 0 && (
            <span className="inline-flex items-center gap-1.5 text-[#C9A86A]" data-testid="receipts-comebacks">
              I came back <Heart className="h-4 w-4" />{comebacks > 1 ? ` × ${comebacks}` : ""}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const FOUNDATION_LINKS = [
  { section: "income", label: "Income", icon: CalendarClock },
  { section: "essentials", label: "Essentials", icon: Wallet },
  { section: "living", label: "Living Bucket", icon: Coins },
  { section: "protection", label: "Protection", icon: PiggyBank },
  { section: "debts", label: "Debts", icon: CreditCard },
];

export function DashboardProfile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const isPasswordUser = (user?.auth_providers || []).includes("password") || false;

  const [name, setName] = useState({ first_name: user?.first_name || "", last_name: user?.last_name || "" });
  const [editingName, setEditingName] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [showPw, setShowPw] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(""); // '' | 'financial' | 'account'
  const [deleteText, setDeleteText] = useState("");
  const [deleteFinText, setDeleteFinText] = useState("");
  const deletingRef = useRef(false); // guards against double-click / double-submit
  const [busy, setBusy] = useState(false);

  useEffect(() => { journeyApi.getNotifications().then(setPrefs).catch(() => {}); }, []);
  useEffect(() => { journeyApi.myRole().then((a) => { setIsAdmin(!!a.is_admin); setIsOwner(!!a.is_owner); }).catch(() => {}); }, []);

  const saveName = async () => {
    setBusy(true);
    try { await journeyApi.updateProfile(name); await refresh(); setEditingName(false); toast.success("Name updated."); }
    catch (e) { toast.error(apiError(e, "Could not update your name.")); }
    finally { setBusy(false); }
  };
  const submitEmail = async () => {
    if (!newEmail.trim()) return toast.error("Enter your new email.");
    setBusy(true);
    try { await journeyApi.changeEmail(newEmail.trim().toLowerCase()); toast.success("Check your new email to confirm the change. We also notified your old email."); setShowEmail(false); setNewEmail(""); }
    catch (e) { toast.error(apiError(e, "Could not start the email change.")); }
    finally { setBusy(false); }
  };
  const submitPassword = async () => {
    setBusy(true);
    try { await journeyApi.changePassword(pw); toast.success("Password changed."); setShowPw(false); setPw({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(apiError(e, "Could not change your password.")); }
    finally { setBusy(false); }
  };
  const toggle = (group, key) => async () => {
    const next = { ...prefs, [group]: { ...prefs[group], [key]: !prefs[group][key] } };
    setPrefs(next);
    try { await journeyApi.setNotifications(next); } catch { toast.error("Could not save preference."); }
  };
  const downloadSummary = async () => {
    try {
      const [d, p] = await Promise.all([journeyApi.exportData(), journeyApi.progress()]);
      const f = d.financial_foundation || {};
      const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const esc = (s) => String(s ?? "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
      const rows = (arr, cols) => (arr || []).map((r) => `<tr>${cols.map((c) => `<td>${esc(typeof c === "function" ? c(r) : r[c])}</td>`).join("")}</tr>`).join("");
      const inc = f.income || {};
      const SLICE = ["#2F6B57", "#C9A86A", "#7FA491", "#164C3B", "#D9C08A", "#A89A88"];
      const compTotal = (p.composition || []).reduce((s, c) => s + c.balance, 0) || 1;
      const compBars = (p.composition || []).length
        ? (p.composition || []).map((c, i) => `<div class="bar"><div class="barlabel"><span>${esc(c.name)}</span><span>${money(c.balance)}</span></div><div class="track"><div class="fill" style="width:${Math.round((c.balance / compTotal) * 100)}%;background:${SLICE[i % SLICE.length]}"></div></div></div>`).join("")
        : `<p class="muted">No remaining consumer debt. Every balance is eliminated.</p>`;
      const perDebt = rows(p.per_debt, [(r) => esc(r.name), (r) => money(r.starting_balance), (r) => money(r.attacked), (r) => money(r.current_balance), (r) => `${r.percent_attacked}%${r.eliminated ? " · eliminated" : ""}`]);
      const prot = p.protection || {};
      const protLine = prot.has_recommendation
        ? (prot.state === "below"
            ? `Protected Runway: <b>${money(prot.funded)}</b> of <b>${money(prot.target)}</b> target (${prot.percent_funded}% funded)`
            : `Protected Runway: <b>${money(prot.funded)}</b> · target met${prot.state === "above" ? ` · ${money(prot.amount_above_target)} above target` : ""}`)
        : `Protected Runway: <b>${money(f.safety?.protected_runway)}</b>`;
      const goal = p.goal;
      const goalBlock = goal
        ? `<h2>Primary Goal</h2><p class="big">${esc(goal.name)} <span class="muted">(${esc(goal.label)})</span></p>
           <p class="muted">Funded ${money(goal.funded)}${goal.open_ended ? " · open-ended" : ` of ${money(goal.target)} · ${goal.percent_funded}% funded · ${money(goal.remaining)} remaining`}</p>`
        : "";
      const mortgageLine = f.mortgage?.has_mortgage
        ? `<p class="muted" style="margin-top:8px">Mortgage principal balance (tracked separately, not in the debt payoff plan): <b style="color:#171717">${money(f.mortgage.balance)}</b></p>`
        : "";
      const lifetime = p.lifetime || {};
      const goalsData = p.goals || { active: goal, completed: [] };
      const completedGoals = goalsData.completed || [];
      const CATLBL = { debt: "Debt", protection: "Protection", build: "BUILD Goal" };
      const canonRows = rows(p.sprint_history, [
        (r) => new Date(r.date).toLocaleDateString(),
        (r) => CATLBL[r.category] || r.category,
        (r) => esc(r.target_name),
        (r) => money(r.amount_confirmed),
        (r) => r.completed_goal ? "Goal complete" : (r.completed_debt ? "Debt eliminated" : ""),
      ]);
      const completedGoalRows = rows(completedGoals, [
        (r) => esc(r.name),
        (r) => esc(r.label || ""),
        (r) => money(r.total_contributed),
        (r) => `${r.completed_count}`,
        (r) => r.completion_date ? new Date(r.completion_date).toLocaleDateString() : "",
      ]);
      const lifetimeBlock = lifetime.money_put_to_work != null
        ? `<h2>Your Paycheck Edit story</h2>
           <div class="hero" style="background:#164C3B;color:#fff">
             <div class="l" style="color:#C9A86A">Total money put to work</div>
             <div class="v" style="color:#fff">${money(lifetime.money_put_to_work)}</div>
             <div class="muted" style="margin-top:8px;color:rgba(255,255,255,.75)">Protected ${money(lifetime.protected)} · Debt attacked ${money(lifetime.debt_attacked)} · Goals built ${money(lifetime.goals_built)}</div>
             <div class="muted" style="margin-top:4px;color:rgba(255,255,255,.75)">Edits completed ${lifetime.confirmed_sprints} · Debts eliminated ${lifetime.debts_eliminated} · Goals completed ${lifetime.goals_completed}</div>
           </div>`
        : "";
      const completedGoalsBlock = completedGoals.length
        ? `<h2>Completed goals</h2><table><tr><th>Goal</th><th>Type</th><th>Total contributed</th><th>Edits</th><th>Completed</th></tr>${completedGoalRows}</table>`
        : "";
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>The Paycheck Edit Financial Progress Summary</title>
      <style>
        body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#171717;margin:40px;background:#fff;}
        .brand{font-size:12px;letter-spacing:.18em;text-transform:uppercase;color:#C9A86A;font-weight:600;}
        h1{font-size:28px;color:#164C3B;margin:4px 0 2px;} h2{font-size:14px;text-transform:uppercase;letter-spacing:.08em;color:#70584A;margin-top:30px;border-bottom:1px solid #E5E0D9;padding-bottom:6px;}
        table{width:100%;border-collapse:collapse;font-size:13px;margin-top:8px;} td,th{text-align:left;padding:7px 8px;border-bottom:1px solid #F0EBE2;} th{color:#70584A;font-size:11px;text-transform:uppercase;letter-spacing:.05em;}
        .muted{color:#8A7C6B;font-size:12px;} .big{font-size:18px;font-weight:600;}
        .cards{display:flex;flex-wrap:wrap;gap:12px;margin-top:10px;} .card{flex:1;min-width:150px;border:1px solid #E5E0D9;border-radius:14px;padding:14px 16px;} .card .l{font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#70584A;} .card .v{font-size:22px;color:#164C3B;font-weight:600;margin-top:4px;}
        .hero{background:#DDE7DF;border-radius:16px;padding:20px 24px;margin-top:12px;} .hero .l{font-size:11px;text-transform:uppercase;letter-spacing:.12em;color:#164C3B;font-weight:600;} .hero .v{font-size:34px;color:#164C3B;font-weight:700;} .hero .track{height:9px;border-radius:9px;background:rgba(255,255,255,.6);overflow:hidden;margin-top:10px;} .hero .fill{height:100%;background:#2F6B57;border-radius:9px;}
        .bar{margin-top:8px;} .barlabel{display:flex;justify-content:space-between;font-size:12px;color:#525252;} .track{height:8px;border-radius:8px;background:#F0EBE2;overflow:hidden;margin-top:3px;} .fill{height:100%;border-radius:8px;}
      </style></head>
      <body onload="window.print()">
        <p class="brand">The Paycheck Edit™</p>
        <h1>Financial Progress Summary</h1>
        <p class="muted">Prepared for ${esc(d.account?.first_name)} ${esc(d.account?.last_name)} (${esc(d.account?.email)}) · Report date ${new Date(d.exported_at).toLocaleDateString()}</p>

        ${lifetimeBlock}

        <div class="hero">
          <div class="l">Total debt attacked</div>
          <div class="v">${money(p.total_attacked)}</div>
          <div class="track"><div class="fill" style="width:${p.percent_attacked}%"></div></div>
          <div class="muted" style="margin-top:8px;color:#2F6B57">${p.percent_attacked}% of your starting ${money(p.starting_debt)} in consumer debt.</div>
        </div>
        <div class="cards">
          <div class="card"><div class="l">Money deployed</div><div class="v">${money(p.sprint_money_deployed)}</div></div>
          <div class="card"><div class="l">Debt Edits</div><div class="v">${p.sprints_completed}</div></div>
          <div class="card"><div class="l">Debts eliminated</div><div class="v">${p.debts_eliminated}</div></div>
          <div class="card"><div class="l">Current debt</div><div class="v">${money(p.current_debt)}</div></div>
        </div>

        <h2>Foundation</h2>
        <p class="big">${esc((inc.type || "").charAt(0).toUpperCase() + (inc.type || "").slice(1))} income${inc.type !== "variable" && inc.take_home_paycheck ? " · " + money(inc.take_home_paycheck) + " expected" : ""}</p>
        <table><tr><th>Essential</th><th>Category</th><th>Amount</th><th>Frequency</th></tr>${rows(f.essentials, ["name", "category", (r) => money(r.amount), "frequency"]) || '<tr><td colspan="4" class="muted">None</td></tr>'}</table>
        <p class="muted" style="margin-top:8px">Living Bucket: <b style="color:#171717">${money(f.living_bucket)}</b> per income cycle</p>

        <h2>Protection snapshot</h2>
        <p>Emergency Fund: <b>${money(f.safety?.emergency_savings)}</b> &nbsp;·&nbsp; ${protLine}</p>
        ${mortgageLine}
        ${goalBlock}
        ${completedGoalsBlock}

        <h2>Debt composition (current)</h2>
        ${compBars}

        <h2>Debt progress by account</h2>
        <table><tr><th>Account</th><th>Starting</th><th>Attacked</th><th>Current</th><th>Eliminated</th></tr>${perDebt || '<tr><td colspan="5" class="muted">No debts recorded</td></tr>'}</table>

        <h2>Edit history</h2>
        <table><tr><th>Date</th><th>Type</th><th>Debt / Goal</th><th>Confirmed</th><th>Milestone</th></tr>${canonRows || '<tr><td colspan="5" class="muted">No confirmed Edits yet</td></tr>'}</table>

        <p class="muted" style="margin-top:34px">The Paycheck Edit™ provides educational financial planning tools. It is not financial, investment, legal, tax, accounting, or credit advice. Debt balances shown reflect only payments you confirmed. Individual results vary and are not guaranteed.</p>
      </body></html>`;
      const w = window.open("", "_blank");
      if (!w) return toast.error("Please allow pop-ups to download your summary.");
      w.document.write(html); w.document.close();
    } catch { toast.error("Could not build your summary."); }
  };
  const downloadWorkbook = async () => {
    try {
      const blob = await journeyApi.exportWorkbook();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = "the-paycheck-edit-summary.xlsx"; a.click(); URL.revokeObjectURL(url);
      track("export_downloaded", { format: "xlsx" });
    } catch { toast.error("Could not build your workbook."); }
  };
  const doDeleteFinancial = async () => {
    if (deleteFinText !== "DELETE") return toast.error("Type DELETE to confirm.");
    if (deletingRef.current) return;
    deletingRef.current = true;
    setBusy(true);
    try {
      await journeyApi.deleteFinancialData();
      // B11: clear any pending/temporary planning state (e.g. an abandoned
      // "Upcoming large bill" Life Happened adjustment) so it can never
      // repopulate the customer's plan after a financial-data reset.
      try { sessionStorage.removeItem("ms_money_landed_draft"); } catch (_) {}
      await refresh();
      toast.success("Your financial data was reset. Rebuild your Foundation to make Edits again.");
      navigate("/onboarding", { replace: true });
    }
    catch (e) { deletingRef.current = false; setBusy(false); return toast.error(apiError(e, "Could not delete your data.")); }
  };
  const doDeleteAccount = async () => {
    if (deleteText !== "DELETE") return toast.error("Type DELETE to confirm.");
    if (deletingRef.current) return; // guard double-click / double-submit
    deletingRef.current = true;
    setBusy(true);
    try {
      await journeyApi.deleteAccount();
    } catch (e) {
      // A 401 here means the session already ended as part of deletion: treat as done.
      if (e?.response?.status !== 401) {
        deletingRef.current = false;
        setBusy(false);
        return toast.error(apiError(e, "Could not delete your account."));
      }
    }
    try { await logout(); } catch (_) {}
    try { sessionStorage.clear(); } catch (_) {}
    toast.success("Your account and financial data have been deleted.");
    window.location.replace("/");
  };

  const cardCls = "rounded-2xl pe-surface p-5";
  const label = "text-xs font-semibold uppercase tracking-[0.1em] text-[#70584A]";
  const field = "mt-1 w-full rounded-xl pe-surface px-3.5 py-2.5 text-base text-[#171717] outline-none focus:border-[#2F6B57] focus:ring-2 focus:ring-[#2F6B57]/25";

  return (
    <DashboardShell>
      <PageHead title="Profile" backToHome />
      <div className="space-y-4">
        {/* ACCOUNT */}
        <div className={cardCls} data-testid="profile-account">
          <div className="flex items-center justify-between">
            <p className={label}>Account</p>
            {!editingName && <button onClick={() => setEditingName(true)} data-testid="edit-name-button" className="text-sm font-medium text-[#2F6B57] hover:underline">Edit name</button>}
          </div>
          {editingName ? (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input value={name.first_name} onChange={(e) => setName({ ...name, first_name: e.target.value })} className={field} placeholder="First name" data-testid="first-name-input" />
                <input value={name.last_name} onChange={(e) => setName({ ...name, last_name: e.target.value })} className={field} placeholder="Last name" data-testid="last-name-input" />
              </div>
              <div className="flex gap-2">
                <Button onClick={saveName} disabled={busy} data-testid="save-name-button" className="min-h-10 rounded-xl bg-[#2F6B57] px-5 font-semibold text-white hover:bg-[#255643]">Save</Button>
                <Button variant="ghost" onClick={() => { setEditingName(false); setName({ first_name: user?.first_name || "", last_name: user?.last_name || "" }); }} className="min-h-10 rounded-xl text-[#70584A]">Cancel</Button>
              </div>
            </div>
          ) : (
            <p className="mt-2 font-medium text-[#171717]">{user?.first_name} {user?.last_name}</p>
          )}
          <div className="mt-3 flex items-center justify-between border-t border-[#F0EBE2] pt-3">
            <div><p className={label}>Email</p><p className="mt-1 text-sm text-[#171717]">{user?.email}</p></div>
            <button onClick={() => setShowEmail((v) => !v)} data-testid="change-email-button" className="text-sm font-medium text-[#2F6B57] hover:underline">Change email</button>
          </div>
          {showEmail && (
            <div className="mt-3 rounded-xl bg-[#F5F2EC] p-3" data-testid="change-email-panel">
              <label className={label}>New email</label>
              <div className="mt-1 flex gap-2">
                <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={field + " mt-0"} placeholder="you@email.com" data-testid="new-email-input" />
                <Button onClick={submitEmail} disabled={busy} data-testid="submit-email-button" className="min-h-11 shrink-0 rounded-xl bg-[#2F6B57] px-4 font-semibold text-white hover:bg-[#255643]">Send</Button>
              </div>
              <p className="mt-2 text-xs text-[#8A7C6B]">We verify the new email and notify your current one. The change applies after you confirm.</p>
            </div>
          )}
        </div>

        {/* SECURITY */}
        <div className={cardCls} data-testid="profile-security">
          <p className={label}>Security</p>
          {isPasswordUser ? (
            !showPw ? (
              <button onClick={() => setShowPw(true)} data-testid="change-password-button" className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-[#2F6B57] hover:underline"><Lock className="h-4 w-4" /> Change password</button>
            ) : (
              <div className="mt-3 space-y-3" data-testid="change-password-panel">
                <PasswordField testid="current-password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} placeholder="Current password" autoComplete="current-password" />
                <PasswordField testid="new-password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="New password (8+ characters)" autoComplete="new-password" />
                <div className="flex gap-2">
                  <Button onClick={submitPassword} disabled={busy} data-testid="save-password-button" className="min-h-10 rounded-xl bg-[#2F6B57] px-5 font-semibold text-white hover:bg-[#255643]">Update password</Button>
                  <Button variant="ghost" onClick={() => setShowPw(false)} className="min-h-10 rounded-xl text-[#70584A]">Cancel</Button>
                </div>
              </div>
            )
          ) : (
            <p className="mt-2 text-sm text-[#70584A]" data-testid="google-account-state">You sign in with Google, so there is no password to manage here. Manage security in your Google account.</p>
          )}
        </div>

        {/* FINANCIAL FOUNDATION */}
        <div className={cardCls} data-testid="profile-foundation">
          <p className={label}>Financial Foundation</p>
          <p className="mt-1 text-sm text-[#A89A88]">Your reusable baseline. Update it any time.</p>
          <div className="mt-3 divide-y divide-[#F0EBE2]">
            {FOUNDATION_LINKS.map(({ section, label: l, icon: Icon }) => (
              <button key={section} type="button" onClick={() => navigate(`/dashboard/foundation/${section}?from=profile`)} data-testid={`profile-edit-${section}`} className="flex w-full items-center justify-between py-3 text-left transition-colors hover:text-[#164C3B]">
                <span className="flex items-center gap-3 text-sm font-medium text-[#171717]"><Icon className="h-4 w-4 text-[#2F6B57]" /> {l}</span>
                <ChevronRight className="h-4 w-4 text-[#A89A88]" />
              </button>
            ))}
          </div>
        </div>

        {/* NOTIFICATIONS */}
        <NotificationsPanel cardCls={cardCls} label={label} />

        {/* SOUND */}
        <SoundToggle />

        {/* HELP & SUPPORT */}
        <div className={cardCls} data-testid="profile-help">
          <p className={label}>Help & support</p>
          <p className="mt-1 text-sm text-[#A89A88]">Answers to common questions and a direct line to our team.</p>
          <div className="mt-3 divide-y divide-[#F0EBE2]">
            <button type="button" onClick={() => navigate("/dashboard/help")} data-testid="profile-help-center" className="flex w-full items-center justify-between py-3 text-left transition-colors hover:text-[#164C3B]">
              <span className="flex items-center gap-3 text-sm font-medium text-[#171717]"><Bell className="h-4 w-4 text-[#2F6B57]" /> Visit the Help Center</span>
              <ChevronRight className="h-4 w-4 text-[#A89A88]" />
            </button>
            {isAdmin && (
              <button type="button" onClick={() => navigate("/dashboard/admin/analytics")} data-testid="profile-admin-analytics" className="flex w-full items-center justify-between py-3 text-left transition-colors hover:text-[#164C3B]">
                <span className="flex items-center gap-3 text-sm font-medium text-[#171717]"><TrendingUp className="h-4 w-4 text-[#2F6B57]" /> Product analytics (admin)</span>
                <ChevronRight className="h-4 w-4 text-[#A89A88]" />
              </button>
            )}
            {isAdmin && (
              <button type="button" onClick={() => navigate("/dashboard/admin/support")} data-testid="profile-admin-support" className="flex w-full items-center justify-between py-3 text-left transition-colors hover:text-[#164C3B]">
                <span className="flex items-center gap-3 text-sm font-medium text-[#171717]"><LifeBuoy className="h-4 w-4 text-[#2F6B57]" /> Customer support console (admin)</span>
                <ChevronRight className="h-4 w-4 text-[#A89A88]" />
              </button>
            )}
            {isOwner && (
              <button type="button" onClick={() => navigate("/dashboard/admin/access")} data-testid="profile-admin-access" className="flex w-full items-center justify-between py-3 text-left transition-colors hover:text-[#164C3B]">
                <span className="flex items-center gap-3 text-sm font-medium text-[#171717]"><ShieldCheck className="h-4 w-4 text-[#2F6B57]" /> Admin access (owner)</span>
                <ChevronRight className="h-4 w-4 text-[#A89A88]" />
              </button>
            )}
          </div>
        </div>

        {/* PRIVACY & DATA */}
        <div className={cardCls} data-testid="profile-privacy">
          <p className={label}>Privacy & data</p>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button onClick={downloadSummary} data-testid="download-summary-button" className="flex items-center gap-2 rounded-xl border border-[#E5E0D9] px-4 py-3 text-left text-sm font-medium text-[#171717] hover:bg-[#F5F2EC]"><Download className="h-4 w-4 text-[#2F6B57]" /> Download My Financial Summary (PDF)</button>
            <button onClick={downloadWorkbook} data-testid="export-xlsx-button" className="flex items-center gap-2 rounded-xl border border-[#E5E0D9] px-4 py-3 text-left text-sm font-medium text-[#171717] hover:bg-[#F5F2EC]"><Download className="h-4 w-4 text-[#2F6B57]" /> Download Excel Workbook (.xlsx)</button>
          </div>

          <div className="mt-4 border-t border-[#F0EBE2] pt-2">
            {confirmDelete === "financial" ? (
              <div className="py-3" data-testid="confirm-financial-panel">
                <div className="flex items-start gap-2 rounded-xl bg-[#F7ECE8] p-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#8B3A2B]" />
                  <p className="text-sm leading-relaxed text-[#171717]">This resets your Financial Foundation and Edit data. Your paid account stays active and you can rebuild your Foundation. This cannot be undone.</p>
                </div>
                <label className="mt-3 block text-sm text-[#70584A]" htmlFor="delete-financial-confirm">Type <span className="font-semibold text-[#171717]">DELETE</span> to confirm</label>
                <input id="delete-financial-confirm" value={deleteFinText} onChange={(e) => setDeleteFinText(e.target.value)} className={field} placeholder="DELETE" data-testid="delete-financial-input" autoComplete="off" />
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <Button onClick={doDeleteFinancial} disabled={busy || deleteFinText !== "DELETE"} data-testid="confirm-delete-financial" className="min-h-10 rounded-xl bg-[#8B3A2B] px-5 font-semibold text-white hover:bg-[#75301f] disabled:opacity-50">DELETE MY FINANCIAL DATA</Button>
                  <Button variant="ghost" onClick={() => { setConfirmDelete(""); setDeleteFinText(""); }} data-testid="cancel-delete-financial" className="min-h-10 rounded-xl text-[#70584A]">CANCEL</Button>
                </div>
              </div>
            ) : (
              <button onClick={() => setConfirmDelete("financial")} data-testid="delete-financial-button" className="flex w-full items-center gap-3 py-3 text-left text-sm font-medium text-[#8B3A2B] hover:underline"><Trash2 className="h-4 w-4 shrink-0" /> Delete my financial data</button>
            )}
          </div>

          <div className="border-t border-[#F0EBE2] pt-2">
            {confirmDelete === "account" ? (
              <div className="py-3" data-testid="confirm-account-panel">
                <div className="flex items-start gap-2 rounded-xl bg-[#F7ECE8] p-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#8B3A2B]" />
                  <p className="text-sm leading-relaxed text-[#171717]">This permanently deletes your Paycheck Edit account and financial data and signs you out. This cannot simply be undone. Some payment records are maintained separately by our payment provider and are not deleted through this action.</p>
                </div>
                <label className="mt-3 block text-sm text-[#70584A]" htmlFor="delete-confirm">Type <span className="font-semibold text-[#171717]">DELETE</span> to confirm</label>
                <input id="delete-confirm" value={deleteText} onChange={(e) => setDeleteText(e.target.value)} className={field} placeholder="DELETE" data-testid="delete-account-input" autoComplete="off" />
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <Button onClick={doDeleteAccount} disabled={busy || deleteText !== "DELETE"} data-testid="confirm-delete-account" className="min-h-10 rounded-xl bg-[#8B3A2B] px-5 font-semibold text-white hover:bg-[#75301f] disabled:opacity-50">DELETE MY ACCOUNT</Button>
                  <Button variant="ghost" onClick={() => { setConfirmDelete(""); setDeleteText(""); }} data-testid="cancel-delete-account" className="min-h-10 rounded-xl text-[#70584A]">CANCEL</Button>
                </div>
              </div>
            ) : (
              <button onClick={() => setConfirmDelete("account")} data-testid="delete-account-button" className="flex w-full items-center gap-3 py-3 text-left text-sm font-medium text-[#8B3A2B] hover:underline"><Trash2 className="h-4 w-4 shrink-0" /> Delete my account</button>
            )}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}


// Accessible switch: role=switch, keyboard operable, state not conveyed by colour alone.
function Switch({ checked, onChange, labelId, testid }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked} aria-labelledby={labelId}
      data-testid={testid} onClick={onChange}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2F6B57] focus-visible:ring-offset-2 ${checked ? "bg-[#2F6B57]" : "bg-[#D6CFC3]"}`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
      <span className="sr-only">{checked ? "On" : "Off"}</span>
    </button>
  );
}

const OPERATIONAL_TOGGLES = [
  ["paycheck_reminders", "Payday reminders", "Get a reminder around an expected regular payday."],
  ["protection_checkins", "Protection check-ins", "Get the check-in you requested after keeping money protected."],
  ["expected_income_checkins", "Expected income check-ins", "Get a reminder when an expected income event reaches its date."],
  ["milestone_messages", "Milestone messages", "Receive important The Paycheck Edit progress celebrations."],
];

const PRETTY_TYPE = { payday: "Payday reminder", expected_income: "Expected income", protection: "Protection check-in" };

export function NotificationsPanel({ cardCls, label }) {
  const [prefs, setPrefs] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [editing, setEditing] = useState(null); // {id, date}
  const [status, setStatus] = useState("");

  const reloadCheckins = () => journeyApi.listCheckins().then((d) => setCheckins(d.checkins || [])).catch(() => {});

  useEffect(() => {
    journeyApi.notifPrefs().then((p) => {
      setPrefs(p);
      // Minimal timezone capture: persist the browser IANA zone once if different.
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz && tz !== p.timezone) journeyApi.saveNotifPrefs({ timezone: tz }).then((np) => setPrefs(np)).catch(() => {});
      } catch {}
    }).catch(() => {});
    reloadCheckins();
  }, []);

  const toggle = (key) => async () => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try { await journeyApi.saveNotifPrefs({ [key]: next[key] }); setStatus(`${key.replace(/_/g, " ")} ${next[key] ? "on" : "off"}.`); }
    catch { toast.error("Could not save preference."); setPrefs(prefs); }
  };

  const saveReschedule = async () => {
    if (!editing?.date) return;
    try { await journeyApi.rescheduleCheckin(editing.id, { date: editing.date }); setEditing(null); setStatus("Check-in updated."); reloadCheckins(); }
    catch { toast.error("Could not change the check-in."); }
  };
  const cancel = async (id) => {
    try { await journeyApi.cancelCheckin(id); setStatus("Check-in cancelled."); reloadCheckins(); }
    catch { toast.error("Could not cancel the check-in."); }
  };

  if (!prefs) return null;

  return (
    <div className={cardCls} data-testid="profile-notifications">
      <div className="flex items-center gap-2"><Bell className="h-4 w-4 text-[#2F6B57]" /><p className={label}>Notifications</p></div>
      <p className="mt-1 text-sm text-[#70584A]">The Paycheck Edit can give you a nudge when it is time to check your next move.</p>

      <p className="mt-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#A89A88]">Operational reminders</p>
      <div className="mt-1 divide-y divide-[#F0EBE2]">
        {OPERATIONAL_TOGGLES.map(([key, title, desc]) => (
          <div key={key} className="flex items-start justify-between gap-4 py-3">
            <div className="min-w-0">
              <p id={`notif-${key}-label`} className="font-medium text-[#171717]">{title}</p>
              <p className="text-sm text-[#70584A]">{desc}</p>
            </div>
            <Switch checked={!!prefs[key]} onChange={toggle(key)} labelId={`notif-${key}-label`} testid={`notif-${key}`} />
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-xl border border-[#E5E0D9] bg-[#FBF9F5] p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#A89A88]">Marketing</p>
        <div className="mt-1 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p id="notif-marketing-label" className="font-medium text-[#171717]">The Paycheck Edit news, offers and updates</p>
            <p className="text-sm text-[#70584A]">Separate from your reminders. Turning reminders on never opts you into marketing.</p>
          </div>
          <Switch checked={!!prefs.marketing_opt_in} onChange={toggle("marketing_opt_in")} labelId="notif-marketing-label" testid="notif-marketing_opt_in" />
        </div>
      </div>

      <p className="mt-6 text-xs font-semibold uppercase tracking-[0.1em] text-[#A89A88]">Upcoming check-ins</p>
      {checkins.length === 0 ? (
        <p className="mt-2 text-sm text-[#70584A]" data-testid="checkins-empty">No check-ins scheduled. You are all caught up.</p>
      ) : (
        <ul className="mt-2 divide-y divide-[#F0EBE2]" data-testid="checkins-list">
          {checkins.map((c) => (
            <li key={c.id} className="py-3" data-testid={`checkin-${c.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-medium text-[#171717]">{c.local_display}</p>
                  <p className="text-sm text-[#70584A]">{PRETTY_TYPE[c.type] || c.label}</p>
                </div>
                <div className="flex shrink-0 gap-3 text-sm">
                  <button type="button" onClick={() => setEditing({ id: c.id, date: c.local_date })} data-testid={`checkin-change-${c.id}`} className="font-medium text-[#2F6B57] hover:underline">Change</button>
                  <button type="button" onClick={() => cancel(c.id)} data-testid={`checkin-cancel-${c.id}`} className="font-medium text-[#8B3A2B] hover:underline">Cancel</button>
                </div>
              </div>
              {editing?.id === c.id && (
                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <label className="sr-only" htmlFor={`checkin-date-${c.id}`}>New check-in date</label>
                  <input id={`checkin-date-${c.id}`} type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} data-testid={`checkin-date-${c.id}`} className="h-10 rounded-xl pe-surface px-3 text-[#171717] focus:border-[#2F6B57] focus:outline-none" />
                  <div className="flex gap-2">
                    <Button onClick={saveReschedule} data-testid={`checkin-save-${c.id}`} className="h-10 rounded-xl bg-[#2F6B57] px-4 text-sm font-semibold text-white hover:bg-[#255643]">Save</Button>
                    <Button variant="ghost" onClick={() => setEditing(null)} className="h-10 rounded-xl px-4 text-sm text-[#70584A]">Cancel</Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <p aria-live="polite" className="sr-only" data-testid="notif-status">{status}</p>
    </div>
  );
}
